import { z } from "zod";
import { VSM_DURATION_UNITS, VSM_STANDARD_UNIT_KEYS } from "@playbookai/types";
import { MediumText, ShortText, idString } from "./common.js";
import { GoalsParamsSchema, defaultGoalsParams } from "./goals.js";
import { TagsParamsSchema, defaultTagsParams } from "./tags.js";
/** New writes use the shared closed Trend vocabulary. */
export const CharterTagsSchema = TagsParamsSchema;
const CharterScopeItemSchema = z
    .object({
    id: idString,
    text: MediumText,
})
    .strict();
const CharterScopeSchema = z
    .object({
    boundary: z.enum(["door-to-door", "end-to-end"]),
    startPoint: MediumText,
    endPoint: MediumText,
    inScope: z.array(CharterScopeItemSchema).max(100),
    outOfScope: z.array(CharterScopeItemSchema).max(100),
})
    .strict();
const NullableNonNegativeNumber = z.number().finite().nonnegative().nullable();
export const CharterTaktInputsSchema = z
    .object({
    unitSystem: z.enum(["manufacturing", "transactional"]),
    period: z.enum(["shift", "day", "week"]),
    grossTime: NullableNonNegativeNumber,
    breaks: NullableNonNegativeNumber,
    plannedStoppages: NullableNonNegativeNumber,
    demand: NullableNonNegativeNumber,
    /** Quantity identity shared with Customer demand on the map. */
    demandUnit: z
        .union([z.enum(VSM_STANDARD_UNIT_KEYS), z.literal("CUSTOM:REQUEST")])
        .optional(),
    shiftsPerDay: z.number().int().positive().nullable(),
    workingDaysPerWeek: z.number().int().min(1).max(7).nullable(),
    /** Absent fields retain the published V14 calculator interpretation. */
    grossTimeUnit: z.enum(VSM_DURATION_UNITS).optional(),
    breaksUnit: z.enum(VSM_DURATION_UNITS).optional(),
    plannedStoppagesUnit: z.enum(VSM_DURATION_UNITS).optional(),
    timeBasis: z.enum(["shift", "day", "week"]).optional(),
    demandBasis: z.enum(["shift", "day", "week"]).optional(),
    downtimeMode: z.enum(["fixed", "percentage"]).optional(),
    taktDisplayUnit: z.enum(VSM_DURATION_UNITS).optional(),
})
    .strict();
/**
 * Authoritative persisted VSM charter params. The feature-local VsmCharter
 * interface remains temporarily for the pre-Wave-3 API adapter only.
 */
export const CharterParamsSchema = z
    .object({
    tags: CharterTagsSchema,
    scope: CharterScopeSchema,
    goals: GoalsParamsSchema,
    takt: CharterTaktInputsSchema,
    calculationMode: z.enum(["manual", "schedule"]).optional(),
    // PB-5215/FEAT-11: retain inactive local data without interpreting it.
    schedule: z.unknown().optional(),
})
    .strict();
export const defaultCharterParams = {
    tags: { ...defaultTagsParams },
    scope: {
        boundary: "door-to-door",
        startPoint: "",
        endPoint: "",
        inScope: [],
        outOfScope: [],
    },
    goals: { ...defaultGoalsParams },
    takt: {
        unitSystem: "manufacturing",
        period: "day",
        grossTime: null,
        breaks: null,
        plannedStoppages: null,
        demand: null,
        shiftsPerDay: null,
        workingDaysPerWeek: null,
    },
};
/** Read compatibility only: does not mutate the supplied value or persist a reset. */
export const StoredCharterParamsSchema = CharterParamsSchema.extend({
    tags: TagsParamsSchema.extend({
        schemaVersion: z.union([z.literal(2), z.literal(3)]),
        trend: ShortText,
    }).transform((tags) => ({
        ...tags,
        schemaVersion: 3,
        trend: TagsParamsSchema.shape.trend.safeParse(tags.trend).data ?? "unknown",
    })),
});
//# sourceMappingURL=charter.js.map